import React from 'react';
import { Form, Input, Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import SelectField from '../../../../common/SelectField';
import { useWorkflowList } from '../../workflow/hooks/use-workflow.hook';
import { useStatusList } from '../../statuses/hooks/use-status.hook';
import type { JobCreatePayload } from '../types/job.types';

interface TemplateJobCreateFormProps {
  visible: boolean;
  onCancel: () => void;
  onSubmit: (data: JobCreatePayload) => Promise<void>;
  loading?: boolean;
}

const TemplateJobCreateForm: React.FC<TemplateJobCreateFormProps> = ({
  visible,
  onCancel,
  onSubmit,
  loading = false,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload: JobCreatePayload = {
        jobName: (values.jobName || '').trim(),
        workflowId: values.workflowId,
        dataIds: [], // Template jobs don't have dataIds
        triggerStatusId: values.triggerStatusId,
        successStatusId: values.successStatusId,
        failedBotStatusId: values.failedBotStatusId,
        failedDataStatusId: values.failedDataStatusId,
        isTemplate: true, // Always true for template jobs
      };
      await onSubmit(payload);
      form.resetFields();
    } catch (error: any) {
      if (error?.errorFields) {
        // Form validation errors - don't show error message, form will show field errors
        return;
      }
      throw error;
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title={t('jobs.template.create.title', { defaultValue: 'Create Template Job' })}
      open={visible}
      onOk={handleSubmit}
      onCancel={handleCancel}
      confirmLoading={loading}
      okText={t('common.create', { defaultValue: 'Create' })}
      cancelText={t('common.cancel', { defaultValue: 'Cancel' })}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="workflowId"
          label={t('jobs.create.workflow.label', { defaultValue: 'Workflow' })}
          rules={[
            {
              required: true,
              message: t('jobs.create.workflow.required', {
                defaultValue: 'Please select a workflow',
              }),
            },
          ]}
        >
          <SelectField
            useListHook={useWorkflowList}
            labelKey="name"
            valueKey="id"
            placeholder={t('jobs.create.workflow.placeholder', {
              defaultValue: 'Select a workflow',
            })}
            required
          />
        </Form.Item>
        <Form.Item
          name="jobName"
          label={t('jobs.create.jobName.label', { defaultValue: 'Job Name' })}
          rules={[
            {
              required: true,
              message: t('jobs.create.jobName.required', {
                defaultValue: 'Please enter a job name',
              }),
            },
            {
              validator: (_: any, value: string) => {
                if (!value || value.trim().length === 0) {
                  return Promise.reject(
                    new Error(
                      t('jobs.create.jobName.required', {
                        defaultValue: 'Please enter a job name',
                      })
                    )
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Input
            placeholder={t('jobs.create.jobName.placeholder', {
              defaultValue: 'Enter job name',
            })}
          />
        </Form.Item>
        <Form.Item
          name="triggerStatusId"
          label={t('jobs.create.triggerStatus.label', { defaultValue: 'Trigger Status' })}
          rules={[
            {
              required: true,
              message: t('jobs.create.triggerStatus.required', {
                defaultValue: 'Trigger status is required',
              }),
            },
          ]}
        >
          <SelectField
            useListHook={useStatusList}
            labelKey="statusCode"
            valueKey="id"
            placeholder={t('jobs.create.triggerStatus.placeholder', {
              defaultValue: 'Trigger Status',
            })}
            required
          />
        </Form.Item>
        <Form.Item
          name="successStatusId"
          label={t('jobs.create.successStatus.label', { defaultValue: 'Success Status' })}
        >
          <SelectField
            useListHook={useStatusList}
            labelKey="statusCode"
            valueKey="id"
            placeholder={t('jobs.create.successStatus.placeholder', {
              defaultValue: 'Select success status',
            })}
          />
        </Form.Item>
        <Form.Item
          name="failedBotStatusId"
          label={t('jobs.create.failedBotStatus.label', { defaultValue: 'Failed Bot Status' })}
        >
          <SelectField
            useListHook={useStatusList}
            labelKey="statusCode"
            valueKey="id"
            placeholder={t('jobs.create.failedBotStatus.placeholder', {
              defaultValue: 'Select failed bot status',
            })}
          />
        </Form.Item>
        <Form.Item
          name="failedDataStatusId"
          label={t('jobs.create.failedDataStatus.label', { defaultValue: 'Failed Data Status' })}
        >
          <SelectField
            useListHook={useStatusList}
            labelKey="statusCode"
            valueKey="id"
            placeholder={t('jobs.create.failedDataStatus.placeholder', {
              defaultValue: 'Select failed data status',
            })}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default TemplateJobCreateForm;

